import React, { useState, useRef, useCallback } from 'react';
import { contentMgmtAPI } from '../../services/api';

const ANNOTATION_TYPES = ['route', 'bracket', 'matchup', 'label', 'highlight', 'zone'];

function AnnotationEditor({ annotation, onChange, onRemove, actorIds }) {
  const update = (key, val) => onChange({ ...annotation, [key]: val });

  return (
    <div className="pe-annotation">
      <div className="pe-annotation-header">
        <select value={annotation.type} onChange={e => update('type', e.target.value)}>
          {ANNOTATION_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <button className="btn-small btn-danger" onClick={onRemove} title="Remove">x</button>
      </div>
      <div className="pe-annotation-fields">
        <label>
          Actors
          <input
            value={(annotation.actors || []).join(', ')}
            onChange={e => update('actors', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
            placeholder="e.g. wr1, slot1"
            list="actor-suggestions"
          />
          <datalist id="actor-suggestions">
            {actorIds.map(id => <option key={id} value={id} />)}
          </datalist>
        </label>
        {annotation.type === 'matchup' && (
          <label>
            Target
            <input
              value={annotation.target || ''}
              onChange={e => update('target', e.target.value)}
              placeholder="e.g. cb1"
              list="actor-suggestions"
            />
          </label>
        )}
        <label>
          Label
          <input
            value={annotation.label || ''}
            onChange={e => update('label', e.target.value)}
            placeholder="Annotation label"
          />
        </label>
        {(annotation.type === 'route' || annotation.type === 'zone') && (
          <div className="pe-position-row">
            <label>
              X
              <input type="number" step="0.5" value={annotation.position?.x ?? ''} onChange={e => update('position', { ...annotation.position, x: parseFloat(e.target.value) || 0 })} />
            </label>
            <label>
              Y
              <input type="number" step="0.5" value={annotation.position?.y ?? ''} onChange={e => update('position', { ...annotation.position, y: parseFloat(e.target.value) || 0 })} />
            </label>
          </div>
        )}
      </div>
    </div>
  );
}

function TimelineEventEditor({ event, index, onChange, actorIds }) {
  const [previewPlaying, setPreviewPlaying] = useState(false);
  const audioRef = useRef(null);

  const update = (key, val) => {
    onChange(index, { ...event, [key]: val });
  };

  const updateAnnotation = (annIdx, updated) => {
    const anns = [...(event.annotations || [])];
    anns[annIdx] = updated;
    update('annotations', anns);
  };

  const removeAnnotation = (annIdx) => {
    const anns = [...(event.annotations || [])];
    anns.splice(annIdx, 1);
    update('annotations', anns);
  };

  const addAnnotation = () => {
    const anns = [...(event.annotations || []), { type: 'route', actors: [], label: '' }];
    update('annotations', anns);
  };

  const previewAudio = useCallback(async () => {
    if (!event.coaching_script) return;
    setPreviewPlaying(true);
    try {
      const { data } = await contentMgmtAPI.ttsPreview(event.coaching_script);
      if (data.audio_url && audioRef.current) {
        audioRef.current.src = data.audio_url;
        audioRef.current.play().catch(() => {});
        audioRef.current.onended = () => setPreviewPlaying(false);
      } else if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
        const utt = new SpeechSynthesisUtterance(event.coaching_script);
        utt.rate = 0.95;
        utt.onend = () => setPreviewPlaying(false);
        window.speechSynthesis.speak(utt);
      } else {
        setPreviewPlaying(false);
      }
    } catch {
      setPreviewPlaying(false);
    }
  }, [event.coaching_script]);

  return (
    <div className="pe-timeline-event" data-testid="pe-timeline-event">
      <div className="pe-event-header">
        <span className="pe-event-badge">{event.event || event.event_id || `Event ${index + 1}`}</span>
        <input
          className="pe-event-name"
          value={event.display_name || ''}
          onChange={e => update('display_name', e.target.value)}
          placeholder="Display name"
        />
        <span className="pe-event-time">{event.time_ms}ms</span>
      </div>

      <div className="pe-coaching-section">
        <label className="pe-label">Coaching Script</label>
        <textarea
          className="pe-coaching-textarea"
          value={event.coaching_script || ''}
          onChange={e => update('coaching_script', e.target.value)}
          placeholder="What the coach voice will say at this moment..."
          rows={3}
        />
        <div className="pe-coaching-actions">
          <button
            className="btn-small"
            onClick={previewAudio}
            disabled={previewPlaying || !event.coaching_script}
          >
            {previewPlaying ? 'Playing...' : 'Preview Audio'}
          </button>
          <span className="pe-char-count">{(event.coaching_script || '').length} chars</span>
        </div>
        <audio ref={audioRef} style={{ display: 'none' }} />
      </div>

      <div className="pe-annotations-section">
        <div className="pe-section-header">
          <label className="pe-label">Annotations ({(event.annotations || []).length})</label>
          <button className="btn-small" onClick={addAnnotation}>+ Add</button>
        </div>
        {(event.annotations || []).map((ann, annIdx) => (
          <AnnotationEditor
            key={annIdx}
            annotation={ann}
            onChange={updated => updateAnnotation(annIdx, updated)}
            onRemove={() => removeAnnotation(annIdx)}
            actorIds={actorIds}
          />
        ))}
      </div>
    </div>
  );
}

export default function PlayEditor({ scenario, onSave, onCancel }) {
  const [data, setData] = useState(() => {
    if (typeof scenario === 'string') {
      try { return JSON.parse(scenario); } catch { return {}; }
    }
    return { ...scenario };
  });
  const [activeTab, setActiveTab] = useState('structured');

  const actorIds = [];
  if (data.timeline) {
    data.timeline.forEach(evt => {
      Object.keys(evt.movements || {}).forEach(id => {
        if (!actorIds.includes(id)) actorIds.push(id);
      });
    });
  }

  const updateEvent = (idx, updatedEvt) => {
    const timeline = [...(data.timeline || [])];
    timeline[idx] = updatedEvt;
    setData({ ...data, timeline });
  };

  const handleSave = () => {
    onSave(data);
  };

  return (
    <div className="play-editor" data-testid="play-editor">
      <div className="pe-header">
        <h3>{data.name || data.play_id || 'Edit Scenario'}</h3>
        <div className="pe-tab-bar">
          <button
            className={`pe-tab ${activeTab === 'structured' ? 'active' : ''}`}
            onClick={() => setActiveTab('structured')}
          >
            Structured
          </button>
          <button
            className={`pe-tab ${activeTab === 'json' ? 'active' : ''}`}
            onClick={() => setActiveTab('json')}
          >
            Raw JSON
          </button>
        </div>
      </div>

      {activeTab === 'structured' ? (
        <div className="pe-structured">
          <div className="pe-meta">
            <label>
              Name
              <input value={data.name || ''} onChange={e => setData({ ...data, name: e.target.value })} />
            </label>
            <label>
              Difficulty
              <select value={data.difficulty || 'beginner'} onChange={e => setData({ ...data, difficulty: e.target.value })}>
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
              </select>
            </label>
            <label>
              Description
              <textarea value={data.description || ''} onChange={e => setData({ ...data, description: e.target.value })} rows={2} />
            </label>
          </div>

          <h4 className="pe-section-title">Timeline Events ({(data.timeline || []).length})</h4>
          {(data.timeline || []).map((evt, idx) => (
            <TimelineEventEditor
              key={idx}
              event={evt}
              index={idx}
              onChange={updateEvent}
              actorIds={actorIds}
            />
          ))}
        </div>
      ) : (
        <textarea
          className="cms-json-editor"
          value={JSON.stringify(data, null, 2)}
          onChange={e => {
            try { setData(JSON.parse(e.target.value)); } catch {}
          }}
          rows={20}
        />
      )}

      <div className="pe-footer">
        <button className="btn-primary" onClick={handleSave}>Save</button>
        <button className="btn-secondary" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}
